/**
 * ArrowConnector - Circular icon container used as a directional connector
 * between two content slots (e.g. source → target, current → proposed).
 *
 * Renders a rounded pill with a centered icon and muted palette. Two sizes
 * cover the current uses: `md` for entity pair layouts (LinkedPair) and
 * `sm` for inline diff rows (DiffSection).
 */

import type { ReactNode } from 'react';

import { cx } from '@/utils/cx';

const SIZE_CLASSES = {
  sm: 'w-5 h-5',
  md: 'w-7 h-7',
} as const;

export interface ArrowConnectorProps {
  /** Container size. `md` (28 px) for entity-pair connectors, `sm` (20 px) for diff rows. */
  size?: 'sm' | 'md';
  /**
   * When true the connector rotates 90° on mobile (vertical stack) and
   * resets on sm+ (horizontal row). Used by LinkedPair where the layout
   * flips from column to row at the sm breakpoint.
   */
  responsiveRotation?: boolean;
  className?: string;
  children: ReactNode;
}

export function ArrowConnector({
  size = 'md',
  responsiveRotation = false,
  className,
  children,
}: ArrowConnectorProps) {
  return (
    <div
      className={cx(
        'flex items-center justify-center rounded-full shrink-0',
        'bg-bg-tertiary text-fg-quaternary',
        SIZE_CLASSES[size],
        responsiveRotation && 'rotate-90 sm:rotate-0',
        className
      )}
      aria-hidden='true'
    >
      {children}
    </div>
  );
}
